import React, { memo, useRef, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import "../../styles/ModalShared.css";
import "../../styles/AppointmentModal.css";
import { useAppointmentStore } from "../../store/useAppointmentStore";
import { useAuthStore } from "../../store/useAuthStore";
import { getShortTimezone } from "../../utils/chatUtils";
import { buildAppointmentHostContract } from "../../utils/appointmentHostContract";
import { useAppSettings } from "../../context/AppSettingsContext";

const AppointmentModalComponent = ({
  isOpen,
  toggle,
  iframeUrl,
  onClose,
  targetOrigin = "*", // Replace with actual origin in production
}) => {
  const iframeRef = useRef(null);
  const hasDataBeenSentRef = useRef(false);
  const isFlutterReadyRef = useRef(false);
  const appointmentDetails = useAppointmentStore((state) => state.appointmentDetails);
  const { authData } = useAuthStore.getState();
  const { eventTimezone } = useAppSettings();
  
  // ✅ Add loading state
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      hasDataBeenSentRef.current = false;
      isFlutterReadyRef.current = false;
      setIsIframeLoading(true);
    }
  }, [isOpen]);

  const handleIframeError = useCallback(
    (event) => {
      if (!iframeUrl) return;
      console.error("❌ AppointmentModal: Failed to load iframe.", {
        src: iframeUrl,
        event,
      });
      setIsIframeLoading(false);
    },
    [iframeUrl]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e?.key === "Escape") {
        onClose?.();
        toggle?.();
      }
    },
    [onClose, toggle]
  );

  // Function to send data to Flutter iframe for scheduling appointment
  const sendDataToFlutter = useCallback(() => {
    // ✅ Null-safety checks
    if (!iframeRef.current?.contentWindow) {
      console.error("❌ [AppointmentModal] Iframe contentWindow not available");
      setIsIframeLoading(false);
      return;
    }

    if (!authData) {
      console.error("❌ [AppointmentModal] authData missing, cannot send data to Flutter.");
      setIsIframeLoading(false);
      return;
    }

    // ✅ Construct message matching Flutter's expected structure
    const message = {
      type: 'appointment_init_data', // Flutter expects this type
      fromNative: false,
      appointment: {
        jwtToken: authData?.accessToken || null, // Required
        channelId: appointmentDetails?.activeChannel?.cid || null, // Required
        targetId: appointmentDetails?.appointmentUser?.id || null, // Required
        name: appointmentDetails?.appointmentUser?.name || '', // User name for summary
        messageId: '', // Empty for new appointments
        isMyMessage: true,
        status: 0, // 0 = new/pending
        date: null, // Will be selected in Flutter UI
        startTime: null, // Will be selected in Flutter UI
        endTime: null, // Will be selected in Flutter UI
        location: null, // Will be entered in Flutter UI
        title: null, // Will be entered in Flutter UI
        note: null, // Will be entered in Flutter UI
        totalMinutes: 0,
        isExpired: false,
        ...buildAppointmentHostContract(), // streamKey, environment, isGatewayEnable
        timezone: getShortTimezone(eventTimezone), // Event timezone abbreviation for Flutter display
      },
      timestamp: new Date().toISOString()
    };
    console.log('📤 [AppointmentModal] #### Sending appointment data to Flutter:', message);
    try {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify(message),
        targetOrigin
      );
      hasDataBeenSentRef.current = true;
    } catch (error) {
      console.error('AppointmentModal] Error sending message to Flutter iframe:', {
        error: error.message,
        stack: error.stack,
        message: message
      });
      setIsIframeLoading(false);
    }
  }, [authData, appointmentDetails, targetOrigin]);

  // ❌ REMOVED: Timer-based data sending
  // ✅ Data will ONLY be sent after receiving 'flutter_ready' message

  // Listen for Flutter messages
  useEffect(() => {
    if (!isOpen) return;

    const handleFlutterMessage = (event) => {
      try {
        if (!event.data) return;

        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        // ✅ Flutter signals it's ready - ONLY NOW send data
        if (data?.type === 'flutter_ready') {
          console.log('✅ [AppointmentModal] Flutter is ready, sending appointment data...');
          isFlutterReadyRef.current = true;
          
          // Send data only if not already sent
          if (!hasDataBeenSentRef.current) {
            sendDataToFlutter();
          }
        }

        // ✅ Flutter confirms data received - HIDE SPINNER
        if (data?.type === 'flutter_data_received') {
          console.log('✅ [AppointmentModal] Flutter confirmed data received - hiding spinner');
          setIsIframeLoading(false);
        }

        // ✅ Alternative: Handle data_loaded confirmation
        if (data?.type === 'data_loaded') {
          console.log('✅ [AppointmentModal] Flutter confirmed data loaded - hiding spinner');
          setIsIframeLoading(false);
        }

        // ✅ Handle data_loaded via appointment_action
        if (data?.type === 'appointment_action' && data?.action === 'data_loaded') {
          console.log('✅ [AppointmentModal] Data loaded via appointment_action - hiding spinner');
          setIsIframeLoading(false);
        }

        // ✅ Handle data load failure
        if (data?.type === 'data_load_failure' || 
            (data?.type === 'appointment_action' && data?.action === 'data_load_failure')) {
          console.error('❌ [AppointmentModal] Flutter failed to load data:', data.error);
          setIsIframeLoading(false);
        }

        // ✅ Handle appointment actions from Flutter
        if (data?.type === 'appointment_action') {
          console.log('📨 [AppointmentModal] Appointment action received:', {
            action: data.action,
            success: data.success,
            timestamp: data.timestamp,
          });

          switch (data.action) {
            case 'created':
            case 'send':
              console.log('✅ [AppointmentModal] Appointment created/saved:', data.appointment);
              if (data.success && data.appointment) {
                // The appointment data from Flutter is now available in data.appointment
                // This contains all the fields: note, location, date, startTime, endTime, etc.
                console.log('📝 Appointment details:', {
                  note: data.appointment.note,
                  location: data.appointment.location,
                  date: data.appointment.date,
                  startTime: data.appointment.startTime,
                  endTime: data.appointment.endTime,
                  totalMinutes: data.appointment.totalMinutes,
                });
                
                // TODO: Send this data to your backend to create the Stream Chat message
                // with the appointment object attached
              }
              onClose?.();
              toggle?.();
              break;

            case 'close':
              console.log('🚪 [AppointmentModal] Closing modal (user cancelled)');
              onClose?.();
              toggle?.();
              break;

            case 'cancel':
              console.log('❌ [AppointmentModal] Appointment cancelled');
              if (data.success) {
                console.log('✅ Appointment cancelled successfully');
              } else {
                console.error('❌ Appointment cancellation failed:', data.error);
              }
              onClose?.();
              toggle?.();
              break; 
            case 'data_loaded':
            case 'data_load_failure':
              // Already handled above
              break;

            default:
              console.warn('⚠️ [AppointmentModal] Unknown appointment action:', data.action);
          }
        }

        // ✅ Handle API request notifications from Flutter
        if (data?.type === 'flutter_api_request') {
          console.log('🌐 [AppointmentModal] Flutter API request:', {
            endpoint: data.endpoint,
            method: data.method,
            origin: data.origin,
          });
        }

        // ✅ Handle iframe resize from Flutter
        if (data?.type === 'resize') {
          const iframe = document.querySelector('.appointment-modal iframe');
          if (iframe) {
            const newHeight = Math.max(data.height || 400, 300); // Min 300px
            iframe.style.height = `${newHeight}px`;
            console.log(`📏 [AppointmentModal] Resized iframe to ${newHeight}px`);
          }
        }
      } catch (err) {
        console.error("❌ AppointmentModal: Failed to handle message from iframe.", {
          error: err.message,
          event: event.data,
        });
        setIsIframeLoading(false);
      }
    };

    window.addEventListener('message', handleFlutterMessage);

    // ✅ Fallback timeout: Hide spinner after 5 seconds if no Flutter response
    const fallbackTimer = setTimeout(() => {
      if (isIframeLoading) {
        console.warn('⏱️ [AppointmentModal] Flutter ready signal timeout (5s), hiding spinner');
        setIsIframeLoading(false);
      }
    }, 5000);

    return () => {
      window.removeEventListener('message', handleFlutterMessage);
      clearTimeout(fallbackTimer);
    };
  }, [isOpen, onClose, toggle, sendDataToFlutter, isIframeLoading]);

  if (!isOpen || !iframeUrl) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="appointment-modal"
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      data-testid="appointment-modal-overlay"
    >
      <ModalBody className="text-center" style={{ position: 'relative' }}>
        {/* ✅ Loading Spinner Overlay */}
        {isIframeLoading && (
          <div className="appointment-modal-spinner-overlay">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading appointment...</span>
            </div>
          </div>
        )}
        
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          title="Schedule Appointment"
          width="100%"
          height="100%"
          allowFullScreen
          onError={handleIframeError}
          aria-label="Schedule Appointment"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        />
      </ModalBody>
    </Modal>
  );
};

AppointmentModalComponent.displayName = "AppointmentModal";

const AppointmentModal = memo(AppointmentModalComponent);

AppointmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  iframeUrl: PropTypes.string,
  title: PropTypes.string,
  onClose: PropTypes.func,
  targetOrigin: PropTypes.string,
};

export default AppointmentModal;